import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { IAcabamento, ISelectOption } from "../types/acabamento.types";
import { TexturaCard } from "./TexturaCard";

// Estilos customizados específicos para esta página - SEM BORDA
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    minHeight: 56,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#3C90AD",
    fontWeight: 500,
    fontSize: "0.8rem",
    "&.Mui-focused": {
      color: "#3C90AD",
    },
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

// FASE 05 — 5ª validação/Frente 2 2026-07-30: nomes dos campos padronizados em azul
// também nos selects (o inputSx já cobria só os TextField).
const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
  "&.Mui-disabled": {
    color: "#3C90AD",
  },
};

interface FormularioAcabamentoProps {
  acabamento: Partial<IAcabamento>;
  onChange: (updates: Partial<IAcabamento>) => void;
  situacoes: ISelectOption[];
  status: ISelectOption[];
  unidades: ISelectOption[];
  tiposAcabamento: ISelectOption[];
  referencias: ISelectOption[];
  // FASE 05 — 6ª validação/PO (item 15): igual aos demais tipos, sinaliza código já existente.
  codigoDuplicado?: boolean;
  // FASE 05 — 6ª validação/PO 2026-08-13: o quadro de Cor/Textura passou a viver dentro do
  // formulário (coluna esquerda), como o quadro de imagem dos demais cadastros N1.
  onOpenModalTextura: () => void;
}

export const FormularioAcabamento: React.FC<FormularioAcabamentoProps> = ({
  acabamento,
  onChange,
  situacoes,
  status,
  unidades,
  tiposAcabamento,
  referencias,
  codigoDuplicado,
  onOpenModalTextura,
}) => {
  const handleChange = (field: keyof IAcabamento, value: any) => {
    onChange({ [field]: value });
  };

  // FASE 05 — 5ª validação/Frente 2 2026-07-30: ordenação conforme mockup do relatório
  // (Situação/Status no topo, Código/Código interno, Descrição, Referência/Tipo,
  // Unidade/Lote mínimo); Situação somente leitura; Referência e Unidade obrigatórios
  // com asterisco (decisão do PO de 30/07).
  return (
    <Box>
      {/* FASE 05 — 6ª validação/PO 2026-08-07 (correção de rumo): Situação (leitura) numa linha
          acima, alinhada horizontalmente com o Status (3ª coluna da linha do Código).
          Cadastro sem imagem / Linha / Grupo. */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth sx={selectSx}>
            <InputLabel id="situacao-label" sx={labelSx}>Situação</InputLabel>
            {/* Situação é somente leitura — transições só pelos botões do fluxo. */}
            <Select
              labelId="situacao-label"
              value={acabamento.idSituacao && situacoes.some(s => s.id === acabamento.idSituacao) ? acabamento.idSituacao : ""}
              label="Situação"
              disabled
            >
              <MenuItem value="">
                <em>Selecione...</em>
              </MenuItem>
              {situacoes.map((situacao) => (
                <MenuItem key={situacao.id} value={situacao.id}>
                  {situacao.nome || situacao.descricao}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* FASE 05 — 6ª validação/PO 2026-08-13: container principal no padrão dos demais
          cadastros N1 — quadro (Cor/Textura) na coluna esquerda e campos na direita. */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Cor / Textura */}
        <Grid item xs={12} md={3}>
          <TexturaCard
            acabamento={acabamento}
            onOpenModal={onOpenModalTextura}
          />
        </Grid>

        {/* Coluna Direita - Campos do Formulário */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {/* Código */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código"
                value={acabamento.codigo || ""}
                onChange={(e) => handleChange("codigo", e.target.value)}
                required
                error={codigoDuplicado}
                helperText={codigoDuplicado ? "Código já existe" : undefined}
                sx={inputSx}
              />
            </Grid>

            {/* Código interno */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código interno"
                value={acabamento.codigoInterno || ""}
                onChange={(e) => handleChange("codigoInterno", e.target.value)}
                sx={inputSx}
              />
            </Grid>

            {/* FASE 05 — 6ª validação/PO 2026-08-07 (correção de rumo): Status na 3ª coluna,
                alinhado com Código/Código interno (Situação fica na linha acima). */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={selectSx}>
                <InputLabel id="status-label" sx={labelSx}>Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={acabamento.idStatus && status.some(s => s.id === acabamento.idStatus) ? acabamento.idStatus : ""}
                  onChange={(e) => handleChange("idStatus", e.target.value === "" ? undefined : Number(e.target.value))}
                  label="Status"
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {status.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.nome || st.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={acabamento.descricao || ""}
                onChange={(e) => handleChange("descricao", e.target.value)}
                required
                sx={inputSx}
              />
            </Grid>

            {/* Referência */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={selectSx} required>
                <InputLabel id="referencia-label" sx={labelSx}>Referência</InputLabel>
                <Select
                  labelId="referencia-label"
                  value={acabamento.idReferencia || ""}
                  onChange={(e) => handleChange("idReferencia", e.target.value ? Number(e.target.value) : undefined)}
                  label="Referência"
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {referencias.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.nome || ref.descricao || ref.codigo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tipo do acabamento */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={selectSx} required>
                <InputLabel id="tipo-acabamento-label" sx={labelSx}>Tipo do acabamento</InputLabel>
                <Select
                  labelId="tipo-acabamento-label"
                  value={acabamento.idTipoTratamento && tiposAcabamento.some(t => t.id === acabamento.idTipoTratamento) ? acabamento.idTipoTratamento : ""}
                  onChange={(e) => handleChange("idTipoTratamento", e.target.value === "" ? undefined : Number(e.target.value))}
                  label="Tipo do acabamento"
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {tiposAcabamento.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>
                      {tipo.nome || tipo.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Unidade */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={selectSx} required>
                <InputLabel id="unidade-label" sx={labelSx}>Unidade</InputLabel>
                <Select
                  labelId="unidade-label"
                  value={acabamento.idUnidade && unidades.some(u => u.id === acabamento.idUnidade) ? acabamento.idUnidade : ""}
                  onChange={(e) => handleChange("idUnidade", e.target.value === "" ? undefined : Number(e.target.value))}
                  label="Unidade"
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {unidades.map((unidade) => (
                    <MenuItem key={unidade.id} value={unidade.id}>
                      {unidade.nome || unidade.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Lote mínimo */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Lote mínimo"
                type="number"
                value={acabamento.loteMinimo ?? ""}
                onChange={(e) => {
                  const value = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 0 && value <= 99999) {
                    handleChange("loteMinimo", value);
                  }
                }}
                inputProps={{ min: 0, max: 99999 }}
                required
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
